import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type {
  ActiveMetric,
  LeaderboardEntry,
} from '@/sections/customerManagement/types';
import {
  METRIC_CALCULATION_INFO,
  METRIC_COLOR_CLASSES,
} from '@/sections/customerManagement/utils/leaderboardTransforms';

import { MetricCell } from './MetricCell';
import { RankDelta } from './RankDelta';

interface Props {
  entries: LeaderboardEntry[];
  activeMetrics: ActiveMetric[];
}

export function LeaderboardTable({ entries, activeMetrics }: Props) {
  return (
    <TooltipProvider>
      <div className="overflow-auto">
        <table
          aria-label="Praetorian leaderboard"
          className="w-full border-collapse text-sm"
        >
          <thead>
            <tr className="border-chariot-stroke border-b text-left">
              <th className="text-chariot-text-secondary px-4 py-3 font-medium">
                Rank
              </th>
              <th className="text-chariot-text-secondary px-4 py-3 font-medium">
                Praetorian
              </th>
              <th className="text-chariot-text-purple px-4 py-3 font-medium">
                Total Score
              </th>
              {activeMetrics.map(m => {
                const description =
                  METRIC_CALCULATION_INFO[m.metricName]?.formula;
                return (
                  <th
                    key={m.metricName}
                    className={cn(
                      'px-4 py-3 text-center font-medium',
                      METRIC_COLOR_CLASSES[m.metricName] ??
                        'text-chariot-text-secondary'
                    )}
                  >
                    {description ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help">{m.displayLabel}</span>
                        </TooltipTrigger>
                        <TooltipContent
                          side="bottom"
                          className="max-w-xs text-xs"
                        >
                          {description}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      m.displayLabel
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {entries.map(entry => (
              <tr
                key={entry.email}
                className="border-chariot-stroke hover:bg-chariot-background-secondary border-b transition-colors"
              >
                <td className="px-4 py-3">
                  <RankDelta rank={entry.rank} delta={entry.rankDelta} />
                </td>
                <td className="px-4 py-3">
                  <div>
                    <span className="text-chariot-text-primary font-medium">
                      {entry.displayName}
                    </span>
                    <span className="text-chariot-text-secondary ml-2 text-xs">
                      {entry.email}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="bg-chariot-text-purple inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-semibold text-white">
                    {entry.totalScore.toFixed(1)}
                  </span>
                </td>
                {activeMetrics.map(m => (
                  <td key={m.metricName} className="px-4 py-3 text-center">
                    <MetricCell
                      metric={entry.metrics[m.metricName]}
                      label={m.displayLabel}
                      colorClass={METRIC_COLOR_CLASSES[m.metricName]}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </TooltipProvider>
  );
}
